import { useFormik } from "formik";
import { useGetInvestorProfile, useUpsertInvestorProfileMutation } from "../api/investor-profile-api";
import Container from "./Container";
import * as Yup from "yup";
import { useRef, useEffect } from "react";
import { Toast } from "primereact/toast";
import FormField from "./FormField";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { Button } from "primereact/button";
import { IIInvestorProfileForm } from "../api/investor-profile-api";
import { AnalyticsEvent, Tracker } from "../analytics";

export const ageRangesOptions = [
    { name: "Até 25 anos", id: "age_up_to_25" },
    { name: "26-35 anos", id: "age_26_35" },
    { name: "36-50 anos", id: "age_36_50" },
    { name: "51-65 anos", id: "age_51_65" },
    { name: "≥ 66 anos", id: "age_over_66" },
];

export const globalInvestmentHorizonOptions = [
    { name: "< 1 ano", id: "horizon_less_1_year" },
    { name: "1-3 anos", id: "horizon_1_3_years" },
    { name: "3-5 anos", id: "horizon_3_5_years" },
    { name: "5-10 anos", id: "horizon_5_10_years" },
    { name: "> 10 anos", id: "horizon_more_10_years" },
];

export const monthlyNetIncomeOptions = [
    { name: "< R$ 5 mil", id: "income_less_5k" },
    { name: "R$ 5–15 mil", id: "income_5_15k" },
    { name: "R$ 15–30 mil", id: "income_15_30k" },
    { name: "R$ 30–60 mil", id: "income_30_60k" },
    { name: "> R$ 60 mil", id: "income_more_60k" },
];

export const marketExperienceOptions = [
    { name: "Nenhuma", id: "experience_none" },
    { name: "Apenas poupança/renda fixa", id: "experience_savings_fixed" },
    { name: "Fundos multimercado", id: "experience_multimarket" },
    { name: "Ações/ETFs", id: "experience_stocks_etfs" },
    { name: "Derivativos/cripto", id: "experience_derivatives_crypto" },
];

export const reactionToLossOptions = [
    { name: "Vender tudo", id: "reaction_sell_all" },
    { name: "Vender parte", id: "reaction_sell_part" },
    { name: "Manter", id: "reaction_hold" },
    { name: "Comprar mais", id: "reaction_buy_more" },
];

export const mainGoalOptions = [
    { name: "Preservar capital", id: "goal_preserve_capital" },
    { name: "Gerar renda", id: "goal_generate_income" },
    { name: "Crescer moderadamente", id: "goal_moderate_growth" },
    { name: "Crescer agressivamente", id: "goal_aggressive_growth" },
];

export const liquidityNeedOptions = [
    { name: "Resgate ≤ 2 dias", id: "liquidity_2_days" },
    { name: "≤ 1 mês", id: "liquidity_1_month" },
    { name: "≤ 6 meses", id: "liquidity_6_months" },
    { name: "≤ 1 ano", id: "liquidity_1_year" },
    { name: "Dispensa liquidez > 1 ano", id: "liquidity_over_1_year" }
];

export const financialKnowledgeOptions = [
    { name: "Muito baixo", id: "knowledge_very_low" },
    { name: "Básico", id: "knowledge_basic" },
    { name: "Intermediário", id: "knowledge_intermediate" },
    { name: "Avançado", id: "knowledge_advanced" },
    { name: "Profissional", id: "knowledge_professional" }
];

export const maxAnnualLossOptions = [
    { name: "0%", id: "loss_0_percent" },
    { name: "≤ 5%", id: "loss_up_to_5_percent" },
    { name: "≤ 10%", id: "loss_up_to_10_percent" },
    { name: "≤ 20%", id: "loss_up_to_20_percent" },
    { name: "> 20%", id: "loss_more_20_percent" },
];

export const illiquidityToleranceOptions = [
    { name: "Preciso poder vender em ≤ 6 meses", id: "illiquidity_6_months" },
    { name: "Até 1 ano", id: "illiquidity_1_year" },
    { name: "1-3 anos", id: "illiquidity_1_3_years" },
    { name: "3-5 anos", id: "illiquidity_3_5_years" },
    { name: "Posso manter por > 5 anos", id: "illiquidity_more_5_years" },
];

export const impactOfRealEstateLossOptions = [
    { name: "Venderia rapidamente", id: "impact_sell_quickly" },
    { name: "Buscaria reduzir exposição", id: "impact_reduce_exposure" },
    { name: "Manteria até recuperação", id: "impact_hold_recovery" },
    { name: "Aproveitaria para comprar mais", id: "impact_buy_more" },
];

export default function InvestorProfileForm({ source = 'profile' }: { source?: string }) {
    const toast = useRef<Toast | null>(null);

    useEffect(() => {
        Tracker.track(AnalyticsEvent.InvestorProfileStarted, { source });
    }, [source]);

    const investorProfileQuery = useGetInvestorProfile();
    const upsertInvestorProfileMutation = useUpsertInvestorProfileMutation(() => {
        if (investorProfileQuery.data?.profile) {
            toast.current?.show({
                severity: "success",
                summary: "Perfil do investidor atualizado com sucesso",
            });
        } else {
            toast.current?.show({
                severity: "success",
                summary: "Perfil do investidor cadastrado com sucesso",
            });
        }

        void investorProfileQuery.refetch().then((result) => {
            Tracker.track(AnalyticsEvent.InvestorProfileCompleted, {
                profile_type: result.data?.profile ?? 'unknown',
            });
        });
    }, (error) => {
        Tracker.trackError('InvestorProfileForm', 'validation');
        toast.current?.show({
            severity: "error",
            summary: error.message,
        });
    });
    
    const formik = useFormik<IIInvestorProfileForm>({
        initialValues: {
            age_range: ageRangesOptions.find(option => option.id === investorProfileQuery.data?.age_range) || { id: "", name: "" },
            global_investment_horizon: globalInvestmentHorizonOptions.find(option => option.id === investorProfileQuery.data?.global_investment_horizon) || { id: "", name: "" },
            monthly_net_income: monthlyNetIncomeOptions.find(option => option.id === investorProfileQuery.data?.monthly_net_income) || { id: "", name: "" },
            new_investment_allocation_pct: investorProfileQuery.data?.new_investment_allocation_pct || 0,
            market_experience: marketExperienceOptions.find(option => option.id === investorProfileQuery.data?.market_experience) || { id: "", name: "" },
            reaction_to_loss: reactionToLossOptions.find(option => option.id === investorProfileQuery.data?.reaction_to_loss) || { id: "", name: "" },
            main_goal: mainGoalOptions.find(option => option.id === investorProfileQuery.data?.main_goal) || { id: "", name: "" },
            liquidity_need: liquidityNeedOptions.find(option => option.id === investorProfileQuery.data?.liquidity_need) || { id: "", name: "" },
            financial_knowledge: financialKnowledgeOptions.find(option => option.id === investorProfileQuery.data?.financial_knowledge) || { id: "", name: "" },
            max_annual_loss: maxAnnualLossOptions.find(option => option.id === investorProfileQuery.data?.max_annual_loss) || { id: "", name: "" },
            illiquidity_tolerance: illiquidityToleranceOptions.find(option => option.id === investorProfileQuery.data?.illiquidity_tolerance) || { id: "", name: "" },
            impact_of_real_estate_loss: impactOfRealEstateLossOptions.find(option => option.id === investorProfileQuery.data?.impact_of_real_estate_loss) || { id: "", name: "" },
        },
        validationSchema: Yup.object().shape({
            age_range: Yup.object().required("Campo obrigatório"),
            global_investment_horizon: Yup.object().required("Campo obrigatório"),
            monthly_net_income: Yup.object().required("Campo obrigatório"),
            new_investment_allocation_pct: Yup.number().required("Campo obrigatório"),
            market_experience: Yup.object().required("Campo obrigatório"),
            reaction_to_loss: Yup.object().required("Campo obrigatório"),
            main_goal: Yup.object().required("Campo obrigatório"),
            liquidity_need: Yup.object().required("Campo obrigatório"),
            financial_knowledge: Yup.object().required("Campo obrigatório"),
            max_annual_loss: Yup.object().required("Campo obrigatório"),
            illiquidity_tolerance: Yup.object().required("Campo obrigatório"),
            impact_of_real_estate_loss: Yup.object().required("Campo obrigatório"),
        }),
        onSubmit: async (values) => {
            await upsertInvestorProfileMutation.mutateAsync({
                age_range: values.age_range.id,
                global_investment_horizon: values.global_investment_horizon.id,
                monthly_net_income: values.monthly_net_income.id,
                new_investment_allocation_pct: values.new_investment_allocation_pct,
                market_experience: values.market_experience.id,
                reaction_to_loss: values.reaction_to_loss.id,
                main_goal: values.main_goal.id,
                liquidity_need: values.liquidity_need.id,
                financial_knowledge: values.financial_knowledge.id,
                max_annual_loss: values.max_annual_loss.id,
                illiquidity_tolerance: values.illiquidity_tolerance.id,
                impact_of_real_estate_loss: values.impact_of_real_estate_loss.id,
            });
        },
    });

    return (
        <>
            <Toast ref={toast} />

            <Container flexColumn gap={4}>
                <h2 className="m-0 mb-4">Perfil do investidor</h2>

                <FormField
                    label="Idade"
                    id="age_range"
                    type="select"
                    hasError={!!formik.errors.age_range && !!formik.touched.age_range}
                    error={formik.errors.age_range as string}
                    children={
                        <Dropdown
                            options={ageRangesOptions}
                            value={formik.values.age_range}
                            optionLabel="name"
                            optionValue="id"
                            onChange={(e) => {
                                formik.setFieldValue("age_range", e.value)
                            }}
                            placeholder="Selecione a faixa etária"
                            invalid={!!formik.errors.age_range && !!formik.touched.age_range}
                            className="w-full"
                        />
                    }
                />

                <FormField
                    label="Horizonte de investimento global"
                    id="global_investment_horizon"
                    type="select"
                    hasError={!!formik.errors.global_investment_horizon && !!formik.touched.global_investment_horizon}
                    error={formik.errors.global_investment_horizon as string}
                    children={
                        <Dropdown
                            options={globalInvestmentHorizonOptions}
                            value={formik.values.global_investment_horizon}
                            optionLabel="name"
                            optionValue="id"
                            onChange={(e) => formik.setFieldValue("global_investment_horizon", e.value)}
                            placeholder="Selecione o horizonte de investimento global"
                            invalid={!!formik.errors.global_investment_horizon && !!formik.touched.global_investment_horizon}
                            className="w-full"
                        />
                    }
                />

                <FormField
                    label="Renda mensal líquida"
                    id="monthly_net_income"
                    type="select"
                    hasError={!!formik.errors.monthly_net_income && !!formik.touched.monthly_net_income}
                    error={formik.errors.monthly_net_income as string}
                    children={
                        <Dropdown
                            options={monthlyNetIncomeOptions}
                            value={formik.values.monthly_net_income}
                            optionLabel="name"
                            optionValue="id"
                            onChange={(e) => formik.setFieldValue("monthly_net_income", e.value)}
                            placeholder="Selecione a renda mensal líquida"
                            invalid={!!formik.errors.monthly_net_income && !!formik.touched.monthly_net_income}
                            className="w-full"
                        />
                    }
                />

                <FormField
                    label="Alocação de investimento nova"
                    id="new_investment_allocation_pct"
                    type="number"
                    hasError={!!formik.errors.new_investment_allocation_pct && formik.touched.new_investment_allocation_pct}
                    error={formik.errors.new_investment_allocation_pct as string}
                    children={
                        <InputNumber
                            value={formik.values.new_investment_allocation_pct}
                            onValueChange={(e) => formik.setFieldValue("new_investment_allocation_pct", e.value)}
                            placeholder="Informe a alocação de investimento nova"
                            maxFractionDigits={2}
                            invalid={!!formik.errors.new_investment_allocation_pct && formik.touched.new_investment_allocation_pct}
                            className="w-full"
                        />
                    }
                />

                <FormField
                    label="Experiência no mercado"
                    id="market_experience"
                    type="select"
                    hasError={!!formik.errors.market_experience && !!formik.touched.market_experience}
                    error={formik.errors.market_experience as string}
                    children={
                        <Dropdown
                            options={marketExperienceOptions}
                            value={formik.values.market_experience}
                            optionLabel="name"
                            optionValue="id"
                            onChange={(e) => formik.setFieldValue("market_experience", e.value)}
                            placeholder="Selecione a experiência no mercado"
                            invalid={!!formik.errors.market_experience && !!formik.touched.market_experience}
                            className="w-full"
                        />
                    }
                />

                <FormField
                    label="Reação à perda"
                    id="reaction_to_loss"
                    type="select"
                    hasError={!!formik.errors.reaction_to_loss && !!formik.touched.reaction_to_loss}
                    error={formik.errors.reaction_to_loss as string}
                    children={
                        <Dropdown
                            options={reactionToLossOptions}
                            value={formik.values.reaction_to_loss}
                            optionLabel="name"
                            optionValue="id"
                            onChange={(e) => formik.setFieldValue("reaction_to_loss", e.value)}
                            placeholder="Selecione a reação à perda"
                            invalid={!!formik.errors.reaction_to_loss && !!formik.touched.reaction_to_loss}
                            className="w-full"
                        />
                    }
                />

                <FormField
                    label="Objetivo principal"
                    id="main_goal"
                    type="select"
                    hasError={!!formik.errors.main_goal && !!formik.touched.main_goal}
                    error={formik.errors.main_goal as string}
                    children={
                        <Dropdown
                            options={mainGoalOptions}
                            value={formik.values.main_goal}
                            optionLabel="name"
                            optionValue="id"
                            onChange={(e) => formik.setFieldValue("main_goal", e.value)}
                            placeholder="Selecione o objetivo principal"
                            invalid={!!formik.errors.main_goal && !!formik.touched.main_goal}
                            className="w-full"
                        />
                    }
                />

                <FormField
                    label="Necessidade de liquidez"
                    id="liquidity_need"
                    type="select"
                    hasError={!!formik.errors.liquidity_need && !!formik.touched.liquidity_need}
                    error={formik.errors.liquidity_need as string}
                    children={
                        <Dropdown
                            options={liquidityNeedOptions}
                            value={formik.values.liquidity_need}
                            optionLabel="name"
                            optionValue="id"
                            onChange={(e) => formik.setFieldValue("liquidity_need", e.value)}
                            placeholder="Selecione a necessidade de liquidez"
                            invalid={!!formik.errors.liquidity_need && !!formik.touched.liquidity_need}
                            className="w-full"
                        />
                    }
                />

                <FormField
                    label="Conhecimento financeiro"
                    id="financial_knowledge"
                    type="select"
                    hasError={!!formik.errors.financial_knowledge && !!formik.touched.financial_knowledge}
                    error={formik.errors.financial_knowledge as string}
                    children={
                        <Dropdown
                            options={financialKnowledgeOptions}
                            value={formik.values.financial_knowledge}
                            optionLabel="name"
                            optionValue="id"
                            onChange={(e) => formik.setFieldValue("financial_knowledge", e.value)}
                            placeholder="Selecione o conhecimento financeiro"
                            invalid={!!formik.errors.financial_knowledge && !!formik.touched.financial_knowledge}
                            className="w-full"
                        />
                    }
                />

                <FormField
                    label="Perda máxima anual"
                    id="max_annual_loss"
                    type="select"
                    hasError={!!formik.errors.max_annual_loss && !!formik.touched.max_annual_loss}
                    error={formik.errors.max_annual_loss as string}
                    children={
                        <Dropdown
                            options={maxAnnualLossOptions}
                            value={formik.values.max_annual_loss}
                            optionLabel="name"
                            optionValue="id"
                            onChange={(e) => formik.setFieldValue("max_annual_loss", e.value)}
                            placeholder="Selecione a perda máxima anual"
                            invalid={!!formik.errors.max_annual_loss && !!formik.touched.max_annual_loss}
                            className="w-full"
                        />
                    }
                />

                <FormField
                    label="Tolerância à liquidez"
                    id="illiquidity_tolerance"
                    type="select"
                    hasError={!!formik.errors.illiquidity_tolerance && !!formik.touched.illiquidity_tolerance}
                    error={formik.errors.illiquidity_tolerance as string}
                    children={
                        <Dropdown
                            options={illiquidityToleranceOptions}
                            value={formik.values.illiquidity_tolerance}
                            optionLabel="name"
                            optionValue="id"
                            onChange={(e) => formik.setFieldValue("illiquidity_tolerance", e.value)}
                            placeholder="Selecione a tolerância à liquidez"
                            invalid={!!formik.errors.illiquidity_tolerance && !!formik.touched.illiquidity_tolerance}
                            className="w-full"
                        />
                    }
                />

                <FormField
                    label="Impacto da perda em imóveis"
                    id="impact_of_real_estate_loss"
                    type="select"
                    hasError={!!formik.errors.impact_of_real_estate_loss && !!formik.touched.impact_of_real_estate_loss}
                    error={formik.errors.impact_of_real_estate_loss as string}
                    children={
                        <Dropdown
                            options={impactOfRealEstateLossOptions}
                            value={formik.values.impact_of_real_estate_loss}
                            optionLabel="name"
                            optionValue="id"
                            onChange={(e) => formik.setFieldValue("impact_of_real_estate_loss", e.value)}
                            placeholder="Selecione o impacto da perda em imóveis"
                            invalid={!!formik.errors.impact_of_real_estate_loss && !!formik.touched.impact_of_real_estate_loss}
                            className="w-full"
                        />
                    }
                />

                <Button 
                    label={investorProfileQuery.data?.profile ? "Editar" : "Cadastrar"}
                    onClick={() => formik.handleSubmit()}
                    icon="pi pi-save"
                    loading={upsertInvestorProfileMutation.isPending}
                    disabled={upsertInvestorProfileMutation.isPending}
                />
            </Container>
        </>
    )
}
